/**
 * "De onde veio a classificação desta menção" (2026-10-08, classificação
 * pronta da planilha).
 *
 * A origem mora ao lado de cada valor — `polaritySource`, `approachSource`
 * e a `source` de cada tema e de cada mensagem-chave — e esta função só a
 * resume numa frase: "Importada da planilha: polaridade, temas · Pelo
 * Reputaition: abordagem". Pura e testada; o detalhe da menção (drawer e
 * "Mais informações") compartilha a mesma frase.
 *
 * **Só fala quando há algo importado.** Sem nada da planilha, a linha de
 * método que a tela já mostra ("Classificado por IA") continua sendo a
 * resposta — acrescentar "Pelo Reputaition: tudo" seria ruído.
 *
 * Quando a menção foi corrigida à mão (`classificationMethod === "manual"`)
 * a metade "pelo Reputaition" cala: a correção troca o valor E apaga a
 * origem, então o que sobra sem origem não é do motor, é de gente.
 */

import type { Mention } from "@/shared/types/mention";
import { canonicalFieldLabel } from "@/shared/vocabulary/canonical-fields";

const IMPORTED = "imported";

type Dimension = "polarity" | "approach" | "themes" | "key_messages";

const ORDER: Dimension[] = ["polarity", "approach", "themes", "key_messages"];

function present(mention: Mention, dimension: Dimension): boolean {
  switch (dimension) {
    case "polarity":
      return mention.polarity !== null;
    case "approach":
      return mention.approachValue !== null;
    case "themes":
      return mention.themes.length > 0;
    case "key_messages":
      return mention.keyMessages.length > 0;
  }
}

function imported(mention: Mention, dimension: Dimension): boolean {
  switch (dimension) {
    case "polarity":
      return mention.polaritySource === IMPORTED;
    case "approach":
      return mention.approachSource === IMPORTED;
    case "themes":
      return mention.themes.some(theme => theme.source === IMPORTED);
    case "key_messages":
      return mention.keyMessages.some(message => message.source === IMPORTED);
  }
}

/** As dimensões que vieram da planilha, na ordem do vocabulário. */
export function importedDimensionsOf(mention: Mention): Dimension[] {
  return ORDER.filter(dimension => imported(mention, dimension));
}

function names(dimensions: Dimension[]): string {
  return dimensions.map(d => canonicalFieldLabel(d).toLowerCase()).join(", ");
}

/** A frase de origem, ou `null` quando nada veio da planilha. */
export function describeOrigin(mention: Mention): string | null {
  const fromSheet = importedDimensionsOf(mention);
  if (fromSheet.length === 0) return null;

  const parts = [`Importada da planilha: ${names(fromSheet)}`];
  if (mention.classificationMethod !== "manual") {
    const fromEngine = ORDER.filter(
      dimension => present(mention, dimension) && !imported(mention, dimension)
    );
    if (fromEngine.length > 0) parts.push(`Pelo Reputaition: ${names(fromEngine)}`);
  }
  return parts.join(" · ");
}

/** O método da classificação em palavras. `manual` faltava no mapa que
 *  morava no detalhe: a menção corrigida à mão mostrava a palavra crua. */
export const CLASSIFICATION_METHOD_LABEL: Record<string, string> = {
  ai: "Classificado por IA",
  deterministic: "Classificado por regras determinísticas",
  both: "Classificado por regras + IA",
  manual: "Corrigida à mão",
  none: "Sem classificação"
};

export function classificationMethodLabel(method: string | null): string {
  if (!method) return "Sem classificação";
  return CLASSIFICATION_METHOD_LABEL[method] ?? method;
}


/** O nome da clipadora como gente lê: o template é cadastrado em
 *  minúscula ("stilingue", "cwa"), e solto no rodapé ele não dizia o que
 *  era. Siglas curtas ficam em caixa alta; o resto, com inicial
 *  maiúscula. Só exibição — o nome gravado não muda. */
export function clippingLabel(name: string | null | undefined): string {
  const value = (name ?? "").trim();
  if (!value) return "clipadora não informada";
  if (value.length <= 4) return value.toUpperCase();
  return value.charAt(0).toUpperCase() + value.slice(1);
}
